import * as yup from 'yup';
import checkICC from '../../../utils/checkICC';
import RESPONSE_CODES from '../../../consts/responseCodes';
import {
  isDefined,
  isArray,
  isStringDefined,
  isUndefined
} from '../../../utils/isDefined';
import { PROVIDERS_NUMBERS } from '../../../consts/initialValues';
import Optional from '../../../utils/optional';
import { translate } from '../../../i18n/I18nProvider';
import checkAvailability from '../../../utils/checkAvailability';

export default function({
  users,
  formNetwork,
  isMobileNumberProvided,
  connect30_domain
}) {
  const dataSharingSimCardNumber = haveSimCardField =>
    yup.string().when(haveSimCardField, {
      is: true,
      then: yup
        .string()
        .required(translate('AUTH.VALIDATION.REQUIRED_FIELD'))
        .matches(/^[0-9]+$/, translate('VALIDATION.ONLY_DIGITS'))
        .min(
          isDefined(checkFormNetwork(formNetwork)) ? 13 : 19,
          `${translate('VALIDATION.TOO_SHORT_NUMBER')}
          ${translate('VALIDATION.DIGITS_LENGTH')} 19-20`
        )
        .max(
          isDefined(checkFormNetwork(formNetwork)) ? 14 : 20,
          `${translate('VALIDATION.TOO_LONG_NUMBER')}
          ${translate('VALIDATION.DIGITS_LENGTH')} 19-20`
        )
        .test(null, 'number is not valid', async function(value) {
          if (isStringDefined(value)) {
            const { status, data } = await checkICC(
              findNumberPrefix(formNetwork, value)
            );
            return [RESPONSE_CODES.SUCCESS].findIndex(
              code => code === status
            ) !== -1
              ? data.Available
                ? true
                : await checkAvailability({
                    data,
                    createError: this.createError,
                    icc: value
                  })
              : true;
          }
        })
    });

  return {
    infoStep: yup.object().shape({
      copy_from_user: yup
        .string()
        .required(translate('AUTH.VALIDATION.REQUIRED_FIELD')),
      email: yup
        .string()
        .email(translate('AUTH.VALIDATION.INVALID_FIELD'))
        .required(translate('AUTH.VALIDATION.REQUIRED_FIELD')),
      first_name: yup
        .string()
        .required(translate('AUTH.VALIDATION.REQUIRED_FIELD')),
      last_name: yup
        .string()
        .required(translate('AUTH.VALIDATION.REQUIRED_FIELD')),
      exchange_id: yup
        .string()
        .email(translate('AUTH.VALIDATION.INVALID_FIELD'))
        .when('exchange_sync', {
          is: true,
          then: yup
            .string()
            .email(translate('AUTH.VALIDATION.INVALID_FIELD'))
            .required(translate('AUTH.VALIDATION.REQUIRED_FIELD'))
        }),
      username: yup
        .string()
        .required(translate('AUTH.VALIDATION.REQUIRED_FIELD'))
        .matches(
          /^[a-z][a-z0-9._-]*$/,
          translate('AUTH.VALIDATION.INVALID_FIELD')
        )
        .test(
          'usernameReserved',
          translate('AUTH.VALIDATION.INVALID_FIELD'),
          function(value) {
            if (!value) return true;
            const reserved = ['conference', 'bulk', 'count'];
            if (reserved.includes(value)) return false;
            return true;
          }
        )
        .test(
          'usernameUnique',
          translate('VALIDATION.USERNAME_ALREADY_EXISTS'),
          function(value) {
            if (!value || !isArray(users)) {
              return true;
            }
            return !users.some(user => user.user_id === value);
          }
        ),
      custom_id: yup
        .string()
        .min(5, translate('VALIDATION.CUSTOM_ID_MIN_LENGTH'))
        .email(translate('VALIDATION.CUSTOM_ID_EMAIL_FORMAT'))
        .test(
          'customIdDifferentFromUsername',
          translate('VALIDATION.CUSTOM_ID_SAME_AS_USERNAME'),
          function(value) {
            if (!value) return true;
            const username = this.parent.username;
            const usernameWithDomain = connect30_domain
              ? `${username}@${connect30_domain}`
              : username;
            return value !== usernameWithDomain;
          }
        )
    }),
    numbersStep: yup
      .object()
      .shape({
        mobile_number: yup
          .string()
          .nullable()
          .matches(/^[0-9]+$/, translate('VALIDATION.ONLY_DIGITS'))
      })
      .test(
        'atLeastOneNumber',
        translate('VALIDATION.AT_LEAST_ONE_NUMBER'),
        function(values) {
          if (
            isStringDefined(values?.landline_number) ||
            isStringDefined(values?.internal_number) ||
            isStringDefined(values?.mobile_number)
          ) {
            return true;
          }
          return this.createError({
            path: 'atLeastOneNumber',
            message: translate('VALIDATION.AT_LEAST_ONE_NUMBER')
          });
        }
      ),
    portStep: yup.object().shape({
      portMobileNumber: yup
        .string()
        .required(translate('AUTH.VALIDATION.REQUIRED_FIELD')),

      move_number_date: yup
        .mixed()
        .nullable()
        .when('is_old_subscripion_date', {
          is: val => !val,
          then: yup
            .mixed()
            .nullable()
            .required(translate('AUTH.VALIDATION.REQUIRED_FIELD'))
        }),

      current_sim_card_number: yup
        .string()
        .required(translate('AUTH.VALIDATION.REQUIRED_FIELD'))
        .test(
          'notEmpty',
          translate('AUTH.VALIDATION.REQUIRED_FIELD'),
          value => !value || value.trim().length > 0
        )
        .matches(/^[0-9]+$/, translate('VALIDATION.ONLY_DIGITS'))
        .min(
          19,
          `${translate('VALIDATION.TOO_SHORT_NUMBER')}
        ${translate('VALIDATION.DIGITS_LENGTH')} 19-20`
        )
        .max(
          20,
          `${translate('VALIDATION.TOO_LONG_NUMBER')}
        ${translate('VALIDATION.DIGITS_LENGTH')} 19-20`
        )
        .test(null, 'number is not valid', async function(value) {
          if (isUndefined(value)) {
            return true;
          }

          const { status, data } = await checkICC(value);
          return [RESPONSE_CODES.SUCCESS].findIndex(code => code === status) !==
            -1
            ? data.Valid
              ? true
              : this.createError({
                  message: translate(
                    `VALIDATE.${data.Message.split(' ')
                      .join('_')
                      .toUpperCase()}`
                  )
                })
            : true;
        }),
      recipient_email: yup
        .string()
        .email(translate('AUTH.VALIDATION.INVALID_FIELD'))
        .required(translate('AUTH.VALIDATION.REQUIRED_FIELD')),
      other_recipient_email: yup
        .string()
        .email(translate('AUTH.VALIDATION.INVALID_FIELD'))
    }),
    subscriptionStep: yup.object().shape({
      mobile_subscription: yup
        .string()
        .when(['isMobileNumberProvided', 'isNewNumber'], {
          is: (isMobileNumberProvided, isNewNumber) =>
            isMobileNumberProvided || isNewNumber,
          then: yup
            .string()
            .required(translate('AUTH.VALIDATION.REQUIRED_FIELD'))
        }),
      mobile_sim_card_number: yup.string().when('have_mobile_sim_card', {
        is: true,
        then: yup
          .string()
          .required(translate('AUTH.VALIDATION.REQUIRED_FIELD'))
          .matches(/^[0-9]+$/, translate('VALIDATION.ONLY_DIGITS'))
          .min(
            isDefined(checkFormNetwork(formNetwork)) ? 13 : 19,
            `${translate('VALIDATION.TOO_SHORT_NUMBER')}
            ${translate('VALIDATION.DIGITS_LENGTH')} 19-20`
          )
          .max(
            isDefined(checkFormNetwork(formNetwork)) ? 14 : 20,
            `${translate('VALIDATION.TOO_LONG_NUMBER')}
            ${translate('VALIDATION.DIGITS_LENGTH')} 19-20`
          )
          .test(null, 'number is not valid', async function(value) {
            if (isStringDefined(value)) {
              const { status, data } = await checkICC(
                findNumberPrefix(formNetwork, value)
              );
              return [RESPONSE_CODES.SUCCESS].findIndex(
                code => code === status
              ) !== -1
                ? data.Available
                  ? true
                  : await checkAvailability({
                      data,
                      createError: this.createError,
                      icc: value
                    })
                : true;
            }
          })
      }),
      data_sharing_sim_card_number: yup
        .string()
        .when('have_data_sharing_sim_card', {
          is: true,
          then: yup
            .string()
            .required(translate('AUTH.VALIDATION.REQUIRED_FIELD'))
            .matches(/^[0-9]+$/, translate('VALIDATION.ONLY_DIGITS'))
            .min(
              isDefined(checkFormNetwork(formNetwork)) ? 13 : 19,
              `${translate('VALIDATION.TOO_SHORT_NUMBER')}
              ${translate('VALIDATION.DIGITS_LENGTH')} 19-20`
            )
            .max(
              isDefined(checkFormNetwork(formNetwork)) ? 14 : 20,
              `${translate('VALIDATION.TOO_LONG_NUMBER')}
              ${translate('VALIDATION.DIGITS_LENGTH')} 19-20`
            )
            .test(null, 'number is not valid', async function(value) {
              if (isStringDefined(value)) {
                const { status, data } = await checkICC(
                  findNumberPrefix(formNetwork, value)
                );
                return [RESPONSE_CODES.SUCCESS].findIndex(
                  code => code === status
                ) !== -1
                  ? data.Available
                    ? true
                    : this.createError({
                        message: translate(
                          `VALIDATE.${data.Message.split(' ')
                            .join('_')
                            .toUpperCase()}`
                        )
                      })
                  : true;
              }
            })
        }),
      extra_card_1_sim_card_number: dataSharingSimCardNumber(
        'extra_card_1_have_sim_card'
      ),
      extra_card_2_sim_card_number: dataSharingSimCardNumber(
        'extra_card_2_have_sim_card'
      ),
      extra_card_3_sim_card_number: dataSharingSimCardNumber(
        'extra_card_3_have_sim_card'
      )
    }),
    addressStep: yup.object().shape({
      order_confirmation_email: yup
        .string()
        .test(
          'commaSeparatedEmails',
          translate('AUTH.VALIDATION.INVALID_FIELD'),
          function(value) {
            if (!isStringDefined(value)) {
              return true;
            }
            const emailSchema = yup.string().email();
            const invalid = value
              .split(',')
              .map(e => e.trim())
              .filter(e => e.length > 0)
              .find(e => !emailSchema.isValidSync(e));
            if (invalid) {
              return this.createError({
                message: translate('VALIDATION.INVALID_EMAIL_IN_LIST').replace(
                  '{email}',
                  invalid
                )
              });
            }
            return true;
          }
        ),
      activation_date: yup
        .string()
        .when(
          ['isMovingDateProvided', 'activationAsap', 'is_old_subscripion_date'],
          {
            is: (
              isMovingDateProvided,
              activationAsap,
              is_old_subscripion_date
            ) =>
              !isMovingDateProvided &&
              !activationAsap &&
              !is_old_subscripion_date &&
              isMobileNumberProvided,
            then: yup
              .string()
              .required(translate('AUTH.VALIDATION.REQUIRED_FIELD'))
          }
        )
        .nullable(),
      name: yup
        .string()
        .when(
          [
            'have_mobile_sim_card',
            'have_data_sharing_sim_card',
            'isMobileNumberProvided',
            'isNewNumber'
          ],
          {
            is: (
              have_mobile_sim_card,
              have_data_sharing_sim_card,
              isMobileNumberProvided,
              isNewNumber
            ) =>
              !have_mobile_sim_card &&
              !have_data_sharing_sim_card &&
              (isMobileNumberProvided || isNewNumber),
            then: yup
              .string()
              .required(translate('AUTH.VALIDATION.REQUIRED_FIELD'))
          }
        )
    })
  };
}

function checkFormNetwork(network) {
  return Optional(PROVIDERS_NUMBERS[network]).or(null);
}

function findNumberPrefix(network, value = '') {
  const networkPrefix = checkFormNetwork(network);
  return isDefined(networkPrefix) ? `${networkPrefix}${value}` : value;
}
